import { useState } from "react";

export function newIdempotencyKey() {
  return globalThis.crypto?.randomUUID?.() ?? `${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

/**
 * A form's Idempotency-Key. Pass it as `idempotent` and the API client keeps one key while the
 * request stays the same, so a double click or a retry after a lost response can't run it twice.
 * A changed request gets a fresh key (the API refuses a key reused for a different request), and
 * so does the next one after any answer but a network failure, 5xx or "still working": a success
 * is done, and a refusal (4xx) created nothing, so a retry must run again rather than replay it.
 */
export class IdempotencyKey {
  private last: { request: string; key: string } | null = null;

  /** The key for this request: the last one if the request is the same, else a fresh one. */
  for(request: unknown): string {
    const s = JSON.stringify(request) ?? "";
    if (this.last?.request !== s) this.last = { request: s, key: newIdempotencyKey() };
    return this.last.key;
  }

  /** Called by the client with the response status (0 for a network failure) and problem code. */
  settle(status: number, code?: string) {
    const unsure = status === 0 || status >= 500 || code === "idempotency_in_progress";
    if (!unsure) this.last = null;
  }
}

/** One IdempotencyKey for the life of a component (a form or a dialog). */
export function useIdempotencyKey(): IdempotencyKey {
  return useState(() => new IdempotencyKey())[0];
}
